import { useEffect, useRef, useState } from "react";
import { LoaderCircle, Search, X } from "lucide-react";
import shopApi from "../../../../api/shopApi";
import { useNotification } from "../../../../hooks/useNotification";

export default function CreateGroupModal({
  isOpen,
  setIsOpen,
  shop,
  contacts,
  selectedContacts,
  setSelectedContacts,
  selectedGroup,
  setSelectedGroup,
}) {
  const { addNotification } = useNotification();
  const [groupName, setGroupName] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const searchRef = useRef(null);
  const [isLoading, setIsLoading] = useState(false);
  const [allContacts, setAllContacts] = useState([]);
  const [isLoadingContacts, setIsLoadingContacts] = useState(false);

  // Fetch all contacts when modal opens
  useEffect(() => {
    if (isOpen && shop?._id) {
      setIsLoadingContacts(true);
      shopApi
        .getContactsWithoutMessages(shop._id)
        .then((res) => {
          if (res.status === 1) {
            // Filter only friends (isFriend = true or type = "friend")
            const friends = res.data.filter(
              (c) => c.isFriend || c.type === "friend",
            );
            setAllContacts(friends);
          } else {
            // Fallback to contacts from props
            setAllContacts(
              contacts.filter((c) => c.isFriend || c.type === "friend"),
            );
          }
          setIsLoadingContacts(false);
        })
        .catch(() => {
          setAllContacts(
            contacts.filter((c) => c.isFriend || c.type === "friend"),
          );
          setIsLoadingContacts(false);
        });
    }
  }, [isOpen, shop?._id]);

  const filteredContacts = allContacts.filter((contact) =>
    contact.name.toLowerCase().includes(searchQuery.toLowerCase()),
  );

  const handleSelectContact = (contact) => {
    if (selectedContacts.find((c) => c._id === contact._id)) {
      setSelectedContacts(
        selectedContacts.filter((c) => c._id !== contact._id),
      );
    } else {
      setSelectedContacts([...selectedContacts, contact]);
    }
  };

  const handleRemoveContact = (contactId) => {
    setSelectedContacts(selectedContacts.filter((c) => c._id !== contactId));
  };

  const handleCancelClick = () => {
    setSearchQuery("");
    setGroupName("");
    setSelectedGroup(null);
    setSelectedContacts([]);
    setIsOpen(false);
  };

  const handleCreateClick = async () => {
    try {
      setIsLoading(true);
      if (selectedGroup) {
        const res = await shopApi.inviteGroup({
          shopId: shop._id,
          uid: selectedGroup.uid,
          memberIds: selectedContacts.map((x) => x.uid),
          memberNames: selectedContacts.map((x) => x.name),
        });
        if (res.status === 1) {
          addNotification("Thêm thành viên vào group thành công", res.status);
          setSearchQuery("");
          setGroupName("");
          setSelectedGroup(null);
          setSelectedContacts([]);
          setIsOpen(false);
        } else addNotification(res.message, res.status);
      } else {
        const gname = groupName?.trim()
          ? groupName.trim()
          : selectedContacts
              .slice(0, 3)
              .map((x) => x.name)
              .join(", ");
        const res = await shopApi.createGroup({
          shopId: shop._id,
          groupName: gname,
          contacts: selectedContacts.map((c) => ({
            uid: c.uid,
            isFriend: c.isFriend,
          })),
        });
        if (res.status === 1) {
          addNotification("Tạo group thành công", res.status);
          setSearchQuery("");
          setGroupName("");
          setSelectedGroup(null);
          setSelectedContacts([]);
          setIsOpen(false);
        } else addNotification(res.message, res.status);
      }
      setIsLoading(false);
    } catch (error) {
      setIsLoading(false);
      addNotification(error.message, -1);
    }
  };

  useEffect(() => {
    if (isOpen) {
      searchRef?.current?.focus();
    }
  }, [isOpen]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 text-black z-20">
      <div className="bg-white w-full max-w-4xl rounded-lg shadow-lg flex flex-col h-[80vh]">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b">
          <h2 className="text-xl font-semibold">
            {selectedGroup ? "Thêm thành viên" : "Tạo nhóm"}
          </h2>
          <button
            onClick={handleCancelClick}
            className="p-1 hover:bg-gray-100 rounded-full"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        {/* Content */}
        <div className="flex flex-1 overflow-hidden">
          {/* Left side: Contact selection */}
          <div className="flex-1 p-4 flex flex-col gap-4 overflow-hidden">
            {/* Group name input */}
            {!selectedGroup && (
              <input
                type="text"
                placeholder="Nhập tên nhóm..."
                value={groupName}
                onChange={(e) => setGroupName(e.target.value)}
                className="w-full p-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            )}

            {/* Contact search */}
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
              <input
                type="text"
                placeholder="Tìm kiếm..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-10 p-2 bg-gray-100 rounded-lg focus:outline-none"
                ref={searchRef}
              />
            </div>

            {/* Friends list */}
            <div className="border rounded-lg overflow-hidden flex-1 flex flex-col">
              <div className="p-3 border-b bg-gray-50">
                <h3 className="font-medium">
                  Danh sách bạn bè{" "}
                  {!isLoadingContacts && `(${allContacts.length})`}
                </h3>
              </div>
              <div className="overflow-y-auto flex-1">
                {isLoadingContacts ? (
                  <div className="flex items-center justify-center p-8">
                    <LoaderCircle
                      className="animate-spin text-blue-600"
                      width={32}
                      height={32}
                    />
                  </div>
                ) : filteredContacts.length === 0 ? (
                  <div className="text-center text-gray-500 p-8">
                    Không tìm thấy bạn bè
                  </div>
                ) : (
                  filteredContacts.map((contact) => (
                    <div
                      key={contact._id}
                      className="flex items-center gap-3 p-2 hover:bg-gray-50 cursor-pointer"
                      onClick={() => handleSelectContact(contact)}
                    >
                      <input
                        type="checkbox"
                        checked={selectedContacts.some(
                          (c) => c._id === contact._id,
                        )}
                        onChange={() => handleSelectContact(contact)}
                        className="w-5 h-5 rounded-sm text-blue-600 focus:ring-blue-500"
                      />
                      <img
                        src={contact.avatar}
                        alt={contact.name}
                        className="w-10 h-10 rounded-full object-cover"
                      />
                      <span>{contact.name}</span>
                    </div>
                  ))
                )}
              </div>
            </div>
          </div>

          {/* Right side: Selected friends */}
          <div className="w-64 border-l p-4 flex flex-col overflow-hidden">
            <h3 className="font-medium mb-2">
              Đã chọn {selectedContacts.length}/100
            </h3>
            <div className="flex-1 overflow-y-auto space-y-2">
              {selectedContacts.map((contact) => (
                <div
                  key={contact._id}
                  className="flex items-center justify-between gap-2 mb-2 bg-blue-100 text-blue-700 px-3 py-2 rounded-lg"
                >
                  <span className="text-sm truncate">{contact.name}</span>
                  <button
                    onClick={() => handleRemoveContact(contact._id)}
                    className="hover:bg-blue-200 rounded-full p-0.5 flex-shrink-0"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="p-4 border-t flex justify-end gap-2">
          <button
            onClick={handleCancelClick}
            className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg"
          >
            Hủy
          </button>
          <button
            onClick={handleCreateClick}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-blue-400 flex items-center"
            disabled={
              (!selectedGroup && selectedContacts.length < 2) || isLoading
            }
          >
            {isLoading && (
              <LoaderCircle
                className="animate-spin text-white mr-2"
                width={20}
                height={20}
              />
            )}
            {selectedGroup ? "Thêm thành viên" : "Tạo nhóm"}
          </button>
        </div>
      </div>
    </div>
  );
}
